import { useEffect, useRef, type RefObject } from 'react';
import { reportRouteExposure, wispClstag } from '@/dataPort/routeTrack';

type TrackPayload = Record<string, any>;

/**
 * 单块内容曝光。阈值对齐搜列：相交即上报一次，同一页面生命周期同一 eventId 不重复。
 * 不在 render 时上报，避免和 Joya 自动 PV 变成同一次“刚挂载就记”。
 */
export function useRouteExposure<T extends HTMLElement>(eventId: string, payload?: TrackPayload, enabled = true) {
  const ref = useRef<T | null>(null);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const reportedEventId = useRef('');
  const clstag = wispClstag(eventId, 'Exposure');

  useEffect(() => {
    const node = ref.current;
    if (!enabled || !node || typeof IntersectionObserver === 'undefined') {
      return;
    }
    if (reportedEventId.current === eventId) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting) || reportedEventId.current === eventId) {
          return;
        }
        reportedEventId.current = eventId;
        reportRouteExposure(eventId, payloadRef.current);
        observer.disconnect();
      },
      { threshold: [0, 0.01] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, eventId]);

  return { ref, clstag };
}

/**
 * 列表曝光。身份用业务 data-exposure-key，不用 DOM 节点本身，避免同一商品重复记。
 */
export function useListExposure(
  containerRef: RefObject<HTMLElement | null>,
  refreshKey: string,
  enabled: boolean,
  onExpose: (exposureKey: string) => void,
) {
  const onExposeRef = useRef(onExpose);
  onExposeRef.current = onExpose;
  const exposedRef = useRef(new Set<string>());

  useEffect(() => {
    const root = containerRef.current;
    if (!enabled || !root || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            return;
          }
          const exposureKey = (entry.target as HTMLElement).dataset.exposureKey;
          if (!exposureKey || exposedRef.current.has(exposureKey)) {
            return;
          }
          exposedRef.current.add(exposureKey);
          onExposeRef.current(exposureKey);
          observer.unobserve(entry.target);
        });
      },
      { threshold: [0, 0.01] },
    );
    root.querySelectorAll<HTMLElement>('[data-exposure-key]').forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, [containerRef, enabled, refreshKey]);
}
